import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Search, Clock, Eye } from 'lucide-react';

function BlogPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Technology', 'Education', 'Career', 'Learning Tips', 'Industry News'];

  const blogPosts = [
    {
      id: 1,
      title: 'The Future of Online Learning: Trends to Watch in 2025',
      excerpt: 'Explore the latest trends shaping the future of online education and how they will impact learners worldwide.',
      content: 'Online learning has evolved dramatically over the past few years, and 2025 promises to bring even more exciting developments...',
      author: 'Sarah Johnson',
      date: '2024-01-15',
      category: 'Education',
      readTime: '5 min read',
      views: '2.3k',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=400&fit=crop',
      tags: ['Online Learning', 'Education Trends', 'Future of Learning']
    },
    {
      id: 2,
      title: 'Mastering React: A Complete Guide for Beginners',
      excerpt: 'Learn React from scratch with this comprehensive guide covering everything from components to hooks.',
      content: 'React has become one of the most popular JavaScript libraries for building user interfaces...',
      author: 'Michael Chen',
      date: '2024-01-12',
      category: 'Technology',
      readTime: '8 min read',
      views: '5.1k',
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&h=400&fit=crop',
      tags: ['React', 'JavaScript', 'Web Development']
    },
    {
      id: 3,
      title: 'Building a Successful Career in Tech: Expert Tips',
      excerpt: 'Industry experts share their insights on how to build and advance your career in the technology sector.',
      content: 'The technology industry offers incredible opportunities for growth and innovation...',
      author: 'David Kim',
      date: '2024-01-10',
      category: 'Career',
      readTime: '6 min read',
      views: '3.7k',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=400&fit=crop',
      tags: ['Career Development', 'Tech Industry', 'Professional Growth']
    },
    {
      id: 4,
      title: '10 Study Techniques That Actually Work',
      excerpt: 'Discover scientifically-proven study techniques that will help you learn more effectively and retain information longer.',
      content: 'Effective studying is not about spending more time, but about using the right techniques...',
      author: 'Lisa Thompson',
      date: '2024-01-08',
      category: 'Learning Tips',
      readTime: '4 min read',
      views: '4.2k',
      image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800&h=400&fit=crop',
      tags: ['Study Techniques', 'Learning', 'Productivity']
    },
    {
      id: 5,
      title: 'AI in Education: How Artificial Intelligence is Transforming Learning',
      excerpt: 'Explore how AI is revolutionizing education and creating personalized learning experiences for students.',
      content: 'Artificial Intelligence is no longer a futuristic concept in education...',
      author: 'Enes Uçar',
      date: '2024-01-05',
      category: 'Industry News',
      readTime: '7 min read',
      views: '6.8k',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=400&fit=crop',
      tags: ['Artificial Intelligence', 'Education Technology', 'Innovation']
    },
    {
      id: 6,
      title: 'The Psychology of Learning: Understanding How We Learn',
      excerpt: 'Dive into the science behind learning and discover how understanding psychology can improve your educational outcomes.',
      content: 'Learning is a complex process that involves multiple cognitive functions...',
      author: 'Sarah Johnson',
      date: '2024-01-03',
      category: 'Education',
      readTime: '6 min read',
      views: '2.9k',
      image: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?w=800&h=400&fit=crop',
      tags: ['Psychology', 'Learning Science', 'Cognitive Science']
    }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = blogPosts[0];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl font-bold text-slate-900 mb-6">
              Our Blog
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Insights, tips, and stories from the world of education and technology. 
              Stay updated with the latest trends and best practices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Featured Article</h2>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="md:flex">
                <div className="md:w-1/2">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-8">
                  <div className="flex items-center space-x-4 text-sm text-slate-600 mb-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                      {featuredPost.category}
                    </span>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {new Date(featuredPost.date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {featuredPost.readTime}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">
                    {featuredPost.title}
                  </h3>
                  
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-slate-400 mr-2" />
                      <span className="text-slate-600">{featuredPost.author}</span>
                    </div>
                    <button className="flex items-center text-blue-600 hover:text-blue-700 font-semibold">
                      Read More
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Latest Articles</h2>
            
            {filteredPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600 text-lg">No articles found matching your criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.slice(1).map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                  >
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover"
                    />
                    
                    <div className="p-6">
                      <div className="flex items-center space-x-4 text-sm text-slate-600 mb-4">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                          {post.category}
                        </span>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(post.date).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-bold text-slate-900 mb-3 line-clamp-2">
                        {post.title}
                      </h3>
                      
                      <p className="text-slate-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-slate-500">
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-1" />
                            {post.author}
                          </div>
                          <div className="flex items-center">
                            <Eye className="w-4 h-4 mr-1" />
                            {post.views}
                          </div>
                        </div>
                        <button className="flex items-center text-blue-600 hover:text-blue-700 font-semibold text-sm">
                          Read More
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </button>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Stay Updated
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Get the latest articles, tips, and insights delivered directly to your inbox. 
              Join thousands of learners who never miss an update.
            </p>
            <div className="max-w-md mx-auto flex gap-4">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-white focus:ring-opacity-50"
              />
              <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-300">
                Subscribe
              </button>
            </div>
            <p className="text-blue-100 text-sm mt-4">
              No spam, unsubscribe at any time.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default BlogPage;
